import { useState } from 'react';
import { ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { MatchingVersion } from '../../../../../services/practiceProductPricing';
import type { DxReportFeeLine } from '../types';
import { versionSelection } from './feeVersion';

export function centsToInputValue(cents: number): string {
    return (cents / 100).toFixed(2);
}

export function inputValueToCents(text: string): number {
    const trimmed = text.trim();
    if (trimmed === '') return 0;
    const dollars = Number(trimmed);
    if (!Number.isFinite(dollars) || dollars < 0) return 0;
    return Math.round(dollars * 100);
}

export function formatCurrency(cents: number): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2
    }).format(cents / 100);
}

interface DraftAmountInputProps {
    cents: number;
    readOnly?: boolean;
    onChange: (cents: number) => void;
    className: string;
}

export function DraftAmountInput({ cents, readOnly, onChange, className }: DraftAmountInputProps) {
    const [draft, setDraft] = useState<string | null>(null);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        // Show a clean number without trailing zeros so the user can edit freely.
        const raw = cents === 0 ? '' : String(cents / 100);
        setDraft(raw);
        // Select-all after focus so a single keystroke replaces the whole value.
        const el = e.target;
        requestAnimationFrame(() => el.select());
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        // Permit empty, whole numbers, and decimals up to 2 places; reject anything else.
        if (/^[0-9]*\.?[0-9]{0,2}$/.test(val)) setDraft(val);
    };

    const commit = () => {
        if (draft === null) return;
        onChange(inputValueToCents(draft));
        setDraft(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.currentTarget.blur();
        }
        if (e.key === 'Escape') {
            setDraft(null);
            e.currentTarget.blur();
        }
    };

    return (
        <input
            type="text"
            inputMode="decimal"
            disabled={readOnly}
            value={draft ?? centsToInputValue(cents)}
            placeholder="0.00"
            onFocus={handleFocus}
            onChange={handleChange}
            onBlur={commit}
            onKeyDown={handleKeyDown}
            className={className}
        />
    );
}

interface PriceInputProps {
    cents: number;
    readOnly?: boolean;
    isOverridden: boolean;
    isStale: boolean;
    resetTargetCents: number | null;
    onChange: (cents: number) => void;
    onReset: () => void;
}

function PriceInput({
    cents,
    readOnly,
    isOverridden,
    isStale,
    resetTargetCents,
    onChange,
    onReset
}: PriceInputProps) {
    const canReset = (isOverridden || isStale) && resetTargetCents != null && !readOnly;
    return (
        <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-sm text-gray-500">$</span>
            <DraftAmountInput
                cents={cents}
                readOnly={readOnly}
                onChange={onChange}
                className={
                    'w-24 appearance-none px-2.5 py-1.5 border rounded-md text-sm tabular-nums text-right focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500 ' +
                    (isOverridden ? 'border-amber-400 bg-amber-50/40' : 'border-gray-300')
                }
            />
            {canReset && (
                <button
                    type="button"
                    onClick={onReset}
                    className="flex items-center justify-center w-6 h-6 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors flex-shrink-0"
                    aria-label="Reset to practice price"
                    title={`Reset to practice price (${formatCurrency(resetTargetCents)})`}
                >
                    <ArrowPathIcon className="w-3 h-3" />
                </button>
            )}
        </div>
    );
}

// Frozen list price + per-rule deltas + effective price, full width to match
// the fee table's rows - hidden once overridden (snapshot no longer matches).
function FeeDiscountBreakdown({ line, compact }: { line: DxReportFeeLine; compact?: boolean }) {
    return (
        <div
            className={`rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 flex flex-col gap-1 ${compact ? 'mt-1 ml-3' : 'mt-1'}`}
        >
            <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">List price</span>
                <span className="text-gray-700 tabular-nums">
                    {formatCurrency(line.listPriceCents ?? 0)}
                </span>
            </div>
            {(line.discounts ?? []).map((d, i) => (
                <div
                    key={`${d.name}-${i}`}
                    className="flex items-center justify-between gap-3 text-xs"
                >
                    <span className="min-w-0 truncate text-gray-600" title={d.name}>
                        {d.name}
                    </span>
                    <span className="text-green-600 tabular-nums shrink-0">
                        -{formatCurrency(d.deltaCents)}
                    </span>
                </div>
            ))}
            <div className="flex items-center justify-between text-xs border-t border-gray-200 pt-1">
                <span className="font-semibold text-gray-800">Effective price</span>
                <span className="font-semibold text-gray-900 tabular-nums">
                    {formatCurrency(line.amountCents)}
                </span>
            </div>
        </div>
    );
}

interface PriceHintProps {
    line: DxReportFeeLine;
    resetTargetCents: number | null;
    compact?: boolean;
}

function PriceHint({ line, resetTargetCents, compact }: PriceHintProps) {
    const className = `text-[10px] text-gray-500 ${compact ? 'ml-3' : 'ml-1'}`;
    if (line.isOverridden) {
        if (resetTargetCents == null) return null;
        return (
            <div className={className}>
                Custom price · practice price:{' '}
                <span className="tabular-nums">{formatCurrency(resetTargetCents)}</span>
            </div>
        );
    }
    if (line.discounts && line.discounts.length > 0 && line.listPriceCents != null) {
        return <FeeDiscountBreakdown line={line} compact={compact} />;
    }
    if (resetTargetCents == null) return null;
    if (resetTargetCents === 0) {
        return <div className={className}>No practice price set - enter a manual amount.</div>;
    }
    return (
        <div className={className}>
            Practice price: <span className="tabular-nums">{formatCurrency(resetTargetCents)}</span>
        </div>
    );
}

interface VersionSelectProps {
    line: DxReportFeeLine;
    versions: MatchingVersion[];
    resolvedVersionId: string | null;
    isStale: boolean;
    readOnly?: boolean;
    onChange: (versionId: string) => void;
}

// Only when several versions match: with one there is nothing to choose.
function VersionSelect({
    line,
    versions,
    resolvedVersionId,
    isStale,
    readOnly,
    onChange
}: VersionSelectProps) {
    if (versions.length < 2) {
        if (!isStale) return null;
        return <div className="mt-1 text-[11px] text-amber-700">Version no longer available</div>;
    }
    const selection = versionSelection(
        { matchingVersions: versions, resolvedVersionId },
        line.versionId
    );
    return (
        <div className="mt-1 flex flex-col gap-0.5">
            <select
                value={selection.value}
                disabled={readOnly}
                onChange={(e) => onChange(e.target.value)}
                aria-label={`${line.productName} version`}
                className="max-w-full px-2 py-1 border border-gray-300 rounded-md bg-white text-xs text-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
            >
                {selection.placeholder && (
                    <option value="" disabled>
                        {selection.placeholder}
                    </option>
                )}
                {versions.map((v) => (
                    <option key={v.id} value={v.id}>
                        {v.label} · {formatCurrency(v.effectivePriceCents ?? v.priceCents)}
                    </option>
                ))}
            </select>
            {selection.needsChoice && (
                <span className="text-[11px] text-amber-700">
                    Several versions match this practice - choose the one to bill.
                </span>
            )}
        </div>
    );
}

interface CaseProductRowProps {
    line: DxReportFeeLine;
    resetTargetCents: number | null;
    versions: MatchingVersion[];
    resolvedVersionId: string | null;
    isStale: boolean;
    readOnly?: boolean;
    onVersionChange: (versionId: string) => void;
    onAmountChange: (cents: number) => void;
    onReset: () => void;
    onClear: () => void;
}

export function CaseProductRow({
    line,
    resetTargetCents,
    versions,
    resolvedVersionId,
    isStale,
    readOnly,
    onVersionChange,
    onAmountChange,
    onReset,
    onClear
}: CaseProductRowProps) {
    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">
                        {line.productName}
                    </div>
                    <VersionSelect
                        line={line}
                        versions={versions}
                        resolvedVersionId={resolvedVersionId}
                        isStale={isStale}
                        readOnly={readOnly}
                        onChange={onVersionChange}
                    />
                </div>
                <PriceInput
                    cents={line.amountCents}
                    readOnly={readOnly}
                    isOverridden={line.isOverridden}
                    isStale={isStale}
                    resetTargetCents={resetTargetCents}
                    onChange={onAmountChange}
                    onReset={onReset}
                />
                {!readOnly && (
                    <button
                        type="button"
                        onClick={onClear}
                        className="flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors flex-shrink-0"
                        aria-label="Clear case product"
                        title="Clear case product"
                    >
                        <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
            <PriceHint line={line} resetTargetCents={resetTargetCents} />
        </div>
    );
}

interface AddonRowProps {
    line: DxReportFeeLine;
    resetTargetCents: number | null;
    versions: MatchingVersion[];
    resolvedVersionId: string | null;
    isStale: boolean;
    readOnly?: boolean;
    onVersionChange: (versionId: string) => void;
    onAmountChange: (cents: number) => void;
    onReset: () => void;
    onRemove: () => void;
}

export function AddonRow({
    line,
    resetTargetCents,
    versions,
    resolvedVersionId,
    isStale,
    readOnly,
    onVersionChange,
    onAmountChange,
    onReset,
    onRemove
}: AddonRowProps) {
    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">
                        {line.productName}
                    </div>
                    <VersionSelect
                        line={line}
                        versions={versions}
                        resolvedVersionId={resolvedVersionId}
                        isStale={isStale}
                        readOnly={readOnly}
                        onChange={onVersionChange}
                    />
                </div>
                <PriceInput
                    cents={line.amountCents}
                    readOnly={readOnly}
                    isOverridden={line.isOverridden}
                    isStale={isStale}
                    resetTargetCents={resetTargetCents}
                    onChange={onAmountChange}
                    onReset={onReset}
                />
                {!readOnly && (
                    <button
                        type="button"
                        onClick={onRemove}
                        className="flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors flex-shrink-0"
                        aria-label={`Remove ${line.productName}`}
                    >
                        <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
            <PriceHint line={line} resetTargetCents={resetTargetCents} compact />
        </div>
    );
}
